import gql from 'graphql-tag';
import { SITE_FRAGMENT, SITE_FRAGMENT_FILTER, SITE_WITH_LOCATION_INFO_FRAGMENT } from '../fragments/site';
import { MICROSTORE_COLUMN_GROUP_WITH_COLUMNS_FRAGMENT } from 'data/fragments/microstoreColumnGroup';
import { STOCK_TRANSFER_FRAGMENT } from '../fragments/stockTransfer';

export const GET_SITES_QUERY = gql`
    query GET_SITES {
        sites {
            _id
            deliveryDays
            deliveryZone {
                _id
                name
                color
            }
            launchDate
            name
            organizationName
            organizationId
            state
            totemId
            type
        }
    }
`;

export const GET_SITES_WITH_REASSORTMENT_STOCK_TRANSFERS_QUERY = gql`
    query GET_SITES_WITH_REASSORTMENT_STOCK_TRANSFERS {
        sites {
            _id
            deliveryDays
            launchDate
            name
            organizationName
            shouldOperateOnTotops
            state
            type
            microstoreColumnGroups {
                _id
                name
                positionInSite
                columns {
                    _id
                    name
                    positionInColumnGroup
                    nextReassortmentStockTransfers {
                        ...StockTransferFragment
                    }
                }
            }
        }
    }
    ${STOCK_TRANSFER_FRAGMENT}
`;

export const GET_SITES_PAGINATED_QUERY = gql`
    query GET_SITES_PAGINATED(
        $offset: Int
        $limit: Int
        $sortBy: String
        $sortOrder: String
        $filters: SitesFilterInput
    ) {
        sitesFilter(offset: $offset, limit: $limit, sortBy: $sortBy, sortOrder: $sortOrder, filters: $filters) {
            hasMore
            sites {
                ...SiteFragmentFilter
            }
        }
    }
    ${SITE_FRAGMENT_FILTER}
`;

export const GET_SIMPLE_MICROSTORE_SITES_QUERY = gql`
    query GET_SIMPLE_MICROSTORE_SITES {
        microstoreSites {
            _id
            organizationName
            microstoreColumnGroups {
                _id
                name
                columns {
                    _id
                    name
                }
            }
            deliveryDays
            launchDate
            name
            state
            type
        }
    }
`;

export const GET_MICROSTORE_SITES_PAGINATED_QUERY = gql`
    query GET_MICROSTORE_SITES_PAGINATED($offset: Int!, $filterString: String, $stateFilter: SiteState) {
        microstoreSitesWithColumnGroupsFilter(offset: $offset, filterString: $filterString, stateFilter: $stateFilter) {
            hasMore
            sites {
                _id
                organizationName
                microstoreColumnGroups {
                    _id
                    name
                    columns {
                        _id
                        name
                    }
                }
                name
                state
                type
            }
        }
    }
    ${SITE_FRAGMENT}
`;

export const GET_MICROSTORE_SITES_QUERY = gql`
    query GET_MICROSTORE_SITES {
        microstoreSitesWithColumnGroups {
            ...SiteFragment
            microstoreColumnGroups {
                _id
                name
                positionInSite
                avaSalePlaceLocationId
                columns {
                    _id
                    siteId
                    columnGroupId
                    columnIdToReplace
                    isPaused
                    microstoreColumnTemplateId
                    name
                    positionInColumnGroup
                    removalDate
                    startingDate
                    microstoreColumnTemplateName
                    type
                }
            }
        }
    }
    ${SITE_FRAGMENT}
`;

export const GET_MICROSTORE_SITES_WITH_REASSORTMENT_STOCK_QUERY = gql`
    query GET_MICROSTORE_SITES_WITH_REASSORTMENT_STOCK {
        microstoreSitesWithReassortmentInfo {
            _id
            freshReassortmentInfos {
                filledLocationsRatio
                hasStartedReassortment
                stockByCategories {
                    dessert
                    main
                    smallBites
                    starter
                }
            }
            name
            nextDeliveryDate
            reassortmentFreshFacings {
                dessert
                main
                smallBites
                starter
            }
            reassortmentStockTargets {
                dessert
                main
                smallBites
                starter
            }
            reassortmentOnboardingStockTargets {
                dessert
                main
                smallBites
                starter
            }
        }
    }
`;

export const GET_SITE_QUERY = gql`
    query GET_SITE($siteId: ID!) {
        site(siteId: $siteId) {
            ...SiteFragment
            microstoreColumnGroups {
                _id
                name
                positionInSite
                columns {
                    _id
                    name
                    state
                    positionInColumnGroup
                    microstoreColumnTemplateName
                    isPaused
                }
            }
            totem {
                _id
                name
                number
                sites {
                    _id
                    name
                }
                organization {
                    _id
                    name
                }
            }
        }
    }
    ${SITE_FRAGMENT}
`;

export const GET_SITE_METABASE_DASHBOARD_URL_QUERY = gql`
    query GET_SITE_METABASE_DASHBOARD_URL($siteId: ID!) {
        siteMetabaseDashboardUrl(siteId: $siteId)
    }
`;

export const GET_MICROSTORE_SITE_QUERY = gql`
    query GET_MICROSTORE_SITE($siteId: ID!) {
        siteWithLocationInfo(siteId: $siteId) {
            ...SiteWithLocationInfoFragment
        }
    }
    ${SITE_WITH_LOCATION_INFO_FRAGMENT}
`;

export const GET_MICROSTORE_SITES_WITH_LOCATIONS_INFO_QUERY = gql`
    query GET_MICROSTORE_SITES_WITH_LOCATIONS_INFO($siteIds: [ID!]!) {
        sites(siteIds: $siteIds) {
            _id
            isSecured
            name
            microstoreColumnGroups {
                ...MicrostoreColumnGroupWithColumnsFragment
            }
        }
    }
    ${MICROSTORE_COLUMN_GROUP_WITH_COLUMNS_FRAGMENT}
`;

export const GET_FRESH_PRODUCTS_WAREHOUSE_STOCK_INFO_QUERY = gql`
    query GET_FRESH_PRODUCTS_WAREHOUSE_STOCK_INFO {
        getFreshProductsWarehouseStockInfo {
            dessert {
                stockAvailableWithReassortment
                stockExpectedInSupplyOrders
                availableWarehouseStock
            }
            main {
                stockAvailableWithReassortment
                stockExpectedInSupplyOrders
                availableWarehouseStock
            }
            smallBites {
                stockAvailableWithReassortment
                stockExpectedInSupplyOrders
                availableWarehouseStock
            }
            starter {
                stockAvailableWithReassortment
                stockExpectedInSupplyOrders
                availableWarehouseStock
            }
        }
    }
`;

export const GET_NEXT_MS_DELIVERY_DATE_QUERY = gql`
    query GET_NEXT_MS_DELIVERY_DATE {
        getNextMSDeliveryDate
    }
`;
